// ✅ PDFCard.js (updated to support taller aspect ratio)
import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Link } from 'react-router-dom';
import MetadataBadges from './ResourceDetail/MetadataBadges';
import { Share2, Heart as HeartIcon, Download, MoreHorizontal, MoreVertical } from 'lucide-react';

const PDFCard = ({ res, compact = false }) => {
  const [validThumbnailUrl, setValidThumbnailUrl] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = React.useRef(null);
  const [isFav, setIsFav] = useState(false);

  const navigate = useNavigate();
  const { setSharePopupProps } = useOutletContext();

  useEffect(() => {
    if (!res || !res.preview_image) {
      setValidThumbnailUrl('/thumbnails/fallback.webp');
    } else {
      setValidThumbnailUrl(`/thumbnails/${res.preview_image}`);
    }
  }, [res]);

  useEffect(() => {
    const favs = JSON.parse(localStorage.getItem('pdf_favorites') || '[]');
    setIsFav(favs.includes(res.code));
  }, [res.code]);

  // Close menu on outside click
  React.useEffect(() => {
    if (!showMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  const handleDownload = (e) => {
    e.stopPropagation();
    const pdfUrl = res.pdf_url || res.pdf;
    if (pdfUrl) {
      window.open(pdfUrl, '_blank', 'noopener');
      setShowMenu(false);
    }
  };

  const handleToggleFav = (e) => {
    e.stopPropagation();
    const favs = JSON.parse(localStorage.getItem('pdf_favorites') || '[]');
    let newFavs;
    if (isFav) {
      newFavs = favs.filter(c => c !== res.code);
    } else {
      newFavs = [...favs, res.code];
    }
    localStorage.setItem('pdf_favorites', JSON.stringify(newFavs));
    setIsFav(!isFav);
    setShowMenu(false);
  };

  if (!res || !res.code || !res.title) return null;

  const title = res.title?.trim() || 'Untitled PDF';

  if (compact) {
    return (
      <div
        className="relative group block cursor-pointer rounded-xl border border-transparent sm:border-gray-100 bg-white p-0.5 shadow-sm sm:shadow hover:shadow-md hover:bg-gray-100 hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 sm:hover:shadow-lg hover:border-blue-300 hover:scale-[1.01] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150 transform w-[180px] h-[180px] sm:w-[220px] sm:h-[240px]"
        aria-labelledby={`desc-${res.code}`}
        style={{ minWidth: 0 }}
      >
        <Link
          to={`/resource/${res.code}`}
          state={{ from: window.location.pathname + window.location.search }}
          onClick={() => {
            const fromUrl = window.location.pathname + window.location.search;
            sessionStorage.setItem("resourceBackLink", fromUrl);
          }}
          className="block w-full h-full focus:outline-none"
          aria-labelledby={`desc-${res.code}`}
        >
          <div className="relative w-full h-full rounded-lg overflow-hidden bg-gray-100">
            <img
              src={validThumbnailUrl || '/thumbnails/fallback.webp'}
              alt={`Thumbnail of ${title}`}
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
            {/* Title overlay at bottom */}
            <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black/70 to-transparent px-2 py-1 rounded-b-xl">
              <h3
                className="font-semibold text-xs text-white leading-tight line-clamp-2 drop-shadow"
                id={`desc-${res.code}`}
                title={title}
                style={{ minHeight: '2.2em' }}
              >
                <span className="bg-yellow-200 bg-opacity-90 rounded px-1 text-gray-900 inline-block">
                  {title}
                </span>
              </h3>
            </div>
          </div>
        </Link>
      </div>
    );
  }

  return (
    <div className="relative group block cursor-pointer rounded-xl border border-transparent sm:border-gray-100 bg-gray-50 p-1 sm:p-2 shadow-sm hover:bg-gray-100 hover:ring-2 hover:ring-blue-300 hover:ring-offset-1 sm:hover:shadow-lg hover:border-blue-300 hover:scale-[1.01] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150 transform" aria-labelledby={`desc-${res.code}`}> 
      {/* Quick actions overlay: three-dot menu */}
      <button
        className="absolute top-2 right-2 z-10 flex items-center justify-center w-7 h-7 rounded-full bg-white/70 hover:bg-gray-100 shadow focus:outline-none focus:ring-2 focus:ring-blue-400"
        aria-label="More actions"
        onClick={e => { e.stopPropagation(); setShowMenu((v) => !v); }}
        tabIndex={0}
      >
        <MoreVertical className="w-5 h-5 text-gray-500" />
      </button>
      {showMenu && (
        <div
          ref={menuRef}
          className="absolute top-10 right-2 z-20 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-2 flex flex-col animate-fadeIn"
          role="menu"
        >
          <button
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-blue-50 focus:bg-blue-100 focus:outline-none text-sm"
            onClick={e => {
              e.stopPropagation();
              setShowMenu(false);
              setSharePopupProps({
                url: `${window.location.origin}/resource/${res.code}`,
                title: res.title,
                mode: 'resource',
                resourceType: res.content_type,
                resourceTitle: res.title,
              });
            }}
            role="menuitem"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-green-50 focus:bg-green-100 focus:outline-none text-sm"
            onClick={e => {
              e.stopPropagation();
              const pdfUrl = res.download_link;
              if (pdfUrl) {
                window.open(pdfUrl, '_blank', 'noopener');
                setShowMenu(false);
              }
            }}
            role="menuitem"
            disabled={!res.download_link}
          >
            <Download className="w-4 h-4" /> Download
          </button>
          <button
            className="flex items-center gap-2 px-4 py-2 text-gray-700 hover:bg-yellow-50 focus:bg-yellow-100 focus:outline-none text-sm"
            onClick={handleToggleFav}
            role="menuitem"
            aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
          >
            <HeartIcon className={`w-4 h-4 ${isFav ? 'text-pink-500 fill-pink-400' : 'text-gray-400'}`} fill={isFav ? 'currentColor' : 'none'} /> Favorite
          </button>
        </div>
      )}
      <Link
        to={`/resource/${res.code}`}
        state={{ from: window.location.pathname + window.location.search }}
        tabIndex="0"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const fromUrl = window.location.pathname + window.location.search;
            navigate(`/resource/${res.code}`, { state: { from: fromUrl } });
          }
        }}
        className="block focus:outline-none"
        aria-labelledby={`desc-${res.code}`}
      >
        <div>
          <div className="relative overflow-hidden rounded-t-xl">
            <img
              src={validThumbnailUrl || '/thumbnails/fallback.webp'}
              alt={`Thumbnail of ${title}`}
              className="w-full aspect-[3/4] object-cover object-top transition-transform duration-200 group-hover:scale-[1.02]"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/thumbnails/fallback.webp';
                e.target.alt = 'No preview available';
              }}
              loading="lazy"
            />
            <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white/80 to-transparent pointer-events-none" />
          </div>
          <div className="px-2 pt-1 pb-2 sm:px-2 sm:pt-1 sm:pb-2 z-10 relative rounded-b-xl">
            <div className="border-t border-gray-200 sm:hidden mb-0.5"></div>
            <h3
              className="block font-bold text-sm sm:text-base tracking-tight text-gray-900 leading-snug my-1 group-hover:underline decoration-blue-400 underline-offset-2 transition-all duration-150"
              id={`desc-${res.code}`}
            >
              <span className="bg-white px-1 py-0.5 rounded shadow-sm">{title}</span>
            </h3>
            <div className="hidden sm:block border-t border-gray-200 mt-0.5 mb-1" />
            <div className="mt-1 flex flex-wrap gap-0.5 group-hover:translate-x-0.5 transition-transform">
              <MetadataBadges resource={res} tabIndex="0" aria-label={`Badges for ${title}`} />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default React.memo(PDFCard, (prev, next) => prev.res.code === next.res.code);